// vorgangFeedFilters.ts — bildet den UI-Spaltenfilter-Zustand der Vorgangsliste
// (filterValues / dateRangeValues / peopleValues / boFoValue) auf die
// serverseitigen Feed-Filter-Parameter (FeedFilterParams) ab (SP3). Rein,
// testbar; kein React/DOM. Leere Werte werden weggelassen, damit die Query
// schlank bleibt und `undefined` konsistent „kein Filter" bedeutet.

import { type DateRangeValue, isPeopleActive, teilePersonenAuswahl } from "../components/redesign/table/filterModel";
import { FEED_AUSTAUSCH_FILTER, type FeedAustauschFilter, type FeedFilterParams } from "../services/vorgangService";

/** Spalten-Ids, unter denen die Filterwerte im DataTable-State liegen. */
const COL_STATUS = "Status";
const COL_BETREFF = "Betreff";
const COL_KONTAKT = "Kontakt";
const COL_BETEILIGTE = "Beteiligte";
const COL_ERSTELLER = "Ersteller";
const COL_FRIST = "Frist";
const COL_TWV = "TWV";
export const COL_AUSTAUSCH = "Austausch";
/**
 * Schlüssel der Personenwahl des Austausch-Filters (PO 01.11.2026, im Filter
 * „Art & Betreff"): Kollegen als userIds unter `peopleValues`, ein Externer
 * als Namensteil unter `filterValues`. Eigene Schlüssel, keine Spalten — der
 * Filter sitzt am Betreff, die Werte gehen als `austauschMit`/`austauschExtern`
 * an den Server.
 */
export const COL_AUSTAUSCH_MIT = "AustauschMit";
export const COL_AUSTAUSCH_EXTERN = "AustauschExtern";

/** Beschriftung der Werte des Austausch-Filters (PO 01.11.2026). */
export const AUSTAUSCH_FILTER_LABEL: Record<FeedAustauschFilter, string> = {
    offen: "Austausch offen",
    mitMir: "Austausch mit mir",
    ohne: "Kein Austausch",
};

/**
 * Rollen des Filters „Beteiligte" (T-245) — Reihenfolge = Anzeige. Die Ids
 * gehen so an die API (`beteiligtRollen`); keine gewählt = alle drei.
 */
export const BETEILIGT_ROLLEN = ["hv", "bearbeiter", "info"] as const;
export const BETEILIGT_ROLLEN_OPTIONEN: { id: string; label: string }[] = [
    { id: "hv", label: "Hauptverantwortlich" },
    { id: "bearbeiter", label: "Bearbeiter" },
    { id: "info", label: "Zu informieren" },
];

export interface FeedFilterInput {
    filterValues: Record<string, string | boolean | undefined>;
    dateRangeValues: Record<string, DateRangeValue | undefined>;
    peopleValues: Record<string, string[] | undefined>;
    boFoValue: { boTeamIds: string[]; foUserIds: string[] };
}

const asText = (v: string | boolean | undefined): string | undefined => {
    if (typeof v !== "string") return undefined;
    const t = v.trim();
    return t ? t : undefined;
};

const asIdList = (v: string[] | undefined): string[] | undefined => {
    const ids = (v ?? []).map((s) => String(s).trim()).filter(Boolean);
    return ids.length ? ids : undefined;
};

const dateVon = (r: DateRangeValue | undefined): string | undefined => {
    const s = r?.von?.trim();
    return s || undefined;
};
const dateBis = (r: DateRangeValue | undefined): string | undefined => {
    const s = r?.bis?.trim();
    return s || undefined;
};

/**
 * UI-Filterzustand → FeedFilterParams. Nur gesetzte Felder erscheinen im
 * Ergebnis (undefined weggelassen), damit `JSON.stringify` als stabiler
 * Refetch-Key taugt und der Server nur echte Filter erhält.
 */
export function buildFeedFilterParams(input: FeedFilterInput): FeedFilterParams {
    const { filterValues, dateRangeValues, peopleValues, boFoValue } = input;
    const out: FeedFilterParams = {};

    const status = asText(filterValues[COL_STATUS]);
    if (status) out.status = status;
    const titel = asText(filterValues[COL_BETREFF]);
    if (titel) out.titel = titel;
    const kontakt = asText(filterValues[COL_KONTAKT]);
    if (kontakt) out.kontakt = kontakt;

    const boTeamIds = asIdList(boFoValue?.boTeamIds);
    if (boTeamIds) out.boTeamIds = boTeamIds;
    const foUserIds = asIdList(boFoValue?.foUserIds);
    if (foUserIds) out.foUserIds = foUserIds;

    // T-245: Im Werte-Array der Spalte stehen neben den User-Ids die gewählten
    // Rollen („rolle:hv" …). Eine Rolle wirkt nur zusammen mit einer Person.
    const { ids: beteiligtIds, rollen } = teilePersonenAuswahl(peopleValues[COL_BETEILIGTE]);
    const beteiligt = asIdList(beteiligtIds);
    if (beteiligt) {
        out.beteiligt = beteiligt;
        const beteiligtRollen = rollen.filter((r) => (BETEILIGT_ROLLEN as readonly string[]).includes(r));
        if (beteiligtRollen.length) out.beteiligtRollen = beteiligtRollen;
    }
    const ersteller = asIdList(peopleValues[COL_ERSTELLER]);
    if (ersteller) out.ersteller = ersteller;

    const austausch = asText(filterValues[COL_AUSTAUSCH]);
    if (austausch && (FEED_AUSTAUSCH_FILTER as readonly string[]).includes(austausch)) {
        out.austausch = austausch as FeedAustauschFilter;
    }
    // Neben „Kein Austausch" hätte eine Person keinen Sinn — sie fällt weg.
    if (out.austausch !== "ohne") {
        const austauschMit = asIdList(peopleValues[COL_AUSTAUSCH_MIT]);
        if (austauschMit) out.austauschMit = austauschMit;
        const austauschExtern = asText(filterValues[COL_AUSTAUSCH_EXTERN]);
        if (austauschExtern) out.austauschExtern = austauschExtern;
    }

    const fristVon = dateVon(dateRangeValues[COL_FRIST]);
    if (fristVon) out.fristVon = fristVon;
    const fristBis = dateBis(dateRangeValues[COL_FRIST]);
    if (fristBis) out.fristBis = fristBis;
    const twvVon = dateVon(dateRangeValues[COL_TWV]);
    if (twvVon) out.twvVon = twvVon;
    const twvBis = dateBis(dateRangeValues[COL_TWV]);
    if (twvBis) out.twvBis = twvBis;

    return out;
}

/**
 * ══ „OHNE FILTER GIBT ES DOCH ETWAS" (PO 21.09.2026) ═══════════════════════
 * > „wenn ich in der tabelle suche und filter sind da, dann wenn keine treffer
 * >  soll er ohne filter suchen und sagen ohne filter ergebnisse, und wenn man
 * >  drauf drückt werden die angezeigt"
 *
 * Eine leere Liste sagt nicht, WARUM sie leer ist. Der häufigste Grund ist ein
 * Filter, den jemand vor einer Stunde gesetzt hat. Statt den Anwender raten zu
 * lassen, zählt die Liste einmal nach und bietet den Weg an.
 *
 * Der Vorfilter „Nächste 14 Tage" ist seit 01.11.2026 entfallen (PO) — er
 * zählte hier mit, weil er beim Einstieg von selbst an war.
 *
 * Der Sichtbarkeitsbereich (Persönlich/Team/Alle) zählt bewusst NICHT mit: Das
 * ist keine Einschränkung der Liste, sondern die Wahl, wessen Arbeit man sieht.
 * Ihn unbemerkt zu weiten wäre eine andere Handlung als „Filter zurücksetzen".
 */
export function zaehlbareFilter(args: {
    filterValues: Record<string, string | boolean | undefined>;
    dateRangeValues: Record<string, DateRangeValue | undefined>;
    peopleValues: Record<string, string[] | undefined>;
    boFoValue: { boTeamIds: string[]; foUserIds: string[] };
}): number {
    const { filterValues, dateRangeValues, peopleValues, boFoValue } = args;
    return (
        Object.values(filterValues).filter((v) => v !== undefined && v !== "" && v !== false).length +
        Object.values(dateRangeValues).filter((r) => !!(r?.von || r?.bis)).length +
        Object.values(peopleValues).filter((ids) => isPeopleActive(ids)).length +
        (boFoValue.boTeamIds.length + boFoValue.foUserIds.length > 0 ? 1 : 0)
    );
}

/** Seit 02.10.2026 gemeinsam mit der Kontaktliste in `hooks/useOhneFilterProbe.ts`. */
export { lohntOhneFilterProbe } from "../hooks/useOhneFilterProbe";
